import { useEffect, useState } from "react";
import {
    Anchor,
    Package,
    PrinterIcon,
    Mail,
    ExternalLink,
    Loader2,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function StatCard({ label, value, accent = false, testid }) {
    return (
        <div
            className={`p-4 rounded-sm border ${
                accent
                    ? "bg-forest text-cream border-forest"
                    : "bg-cream-warm text-forest border-forest/10"
            }`}
            data-testid={testid}
        >
            <div
                className={`text-[0.55rem] tracking-[0.32em] uppercase ${
                    accent ? "text-gold" : "text-brown/70"
                }`}
            >
                {label}
            </div>
            <div className="font-serif text-2xl mt-1">{value}</div>
        </div>
    );
}

function FulfillmentRow({ row, onLabel, onNotify, busyId }) {
    const label = row.label || {};
    const isLabeled = row.label_status === "labeled";
    const isNotified = !!row.notified_at;
    return (
        <div
            className="p-4 bg-cream rounded-sm border border-forest/10"
            data-testid={`privateer-row-${row.id}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                    <div className="font-serif text-forest text-base">
                        {(row.line_items || [])
                            .map((l) => `${l.name} ×${l.quantity}`)
                            .join(" · ") || "—"}
                    </div>
                    <div className="text-[0.6rem] tracking-[0.25em] uppercase text-brown/70 mt-1">
                        Order {(row.order_id || "").slice(0, 8)} ·{" "}
                        {row.customer_email || "guest"}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {isLabeled ? (
                        <span className="text-[0.55rem] tracking-[0.3em] uppercase px-2 py-1 rounded-full bg-olive/20 text-forest border border-olive/40">
                            Labeled
                        </span>
                    ) : (
                        <span className="text-[0.55rem] tracking-[0.3em] uppercase px-2 py-1 rounded-full bg-gold/20 text-brown border border-gold/60">
                            Awaiting Label
                        </span>
                    )}
                    {isNotified && (
                        <span className="text-[0.55rem] tracking-[0.3em] uppercase px-2 py-1 rounded-full bg-forest text-gold border border-gold/60">
                            Notified
                        </span>
                    )}
                </div>
            </div>

            {isLabeled && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    <div className="text-xs text-brown">
                        <span className="text-[0.55rem] tracking-[0.3em] uppercase text-brown/60">
                            Carrier
                        </span>
                        <div className="font-serif text-forest text-base">
                            {label.carrier || "—"}{" "}
                            {label.service_level && (
                                <span className="text-brown/60 text-xs ml-1">
                                    {label.service_level}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="text-xs text-brown">
                        <span className="text-[0.55rem] tracking-[0.3em] uppercase text-brown/60">
                            Tracking · ${label.cost?.toFixed?.(2) ?? "0.00"}
                        </span>
                        <div className="font-mono text-forest text-sm mt-0.5">
                            {label.tracking_number || "—"}
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => onLabel(row.id)}
                    disabled={busyId === row.id}
                    className="btn-pill btn-ghost text-xs disabled:opacity-50"
                    data-testid={`privateer-label-btn-${row.id}`}
                >
                    {busyId === row.id ? (
                        <>
                            <Loader2 size={12} className="animate-spin" /> Working
                        </>
                    ) : (
                        <>
                            <PrinterIcon size={12} />{" "}
                            {isLabeled ? "Re-print Label" : "Generate Label"}
                        </>
                    )}
                </button>

                {label.tracking_url && (
                    <a
                        href={label.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-pill btn-ghost text-xs"
                        data-testid={`privateer-track-link-${row.id}`}
                    >
                        Track <ExternalLink size={12} />
                    </a>
                )}

                <button
                    type="button"
                    onClick={() => onNotify(row.id)}
                    disabled={busyId === row.id || !isLabeled}
                    className="btn-pill btn-gold text-xs disabled:opacity-40"
                    data-testid={`privateer-notify-btn-${row.id}`}
                >
                    <Mail size={12} /> Vessel in Transit
                </button>
            </div>

            {row.notify_result && row.notify_result.mocked && (
                <p className="mt-2 text-[0.6rem] tracking-[0.25em] uppercase text-brown/60 italic">
                    Last notify mocked · reason: {row.notify_result.reason}
                </p>
            )}
        </div>
    );
}

export default function Privateer() {
    const [data, setData] = useState({
        items: [],
        counts: {},
        printify_configured: false,
        shippo_configured: false,
        resend_configured: false,
    });
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const { data: res } = await api.get("/admin/fulfillment/queue");
            setData(res);
        } catch {
            toast.error("Could not load fulfillment queue");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onLabel = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/admin/fulfillment/${id}/generate-label`, {});
            toast.success("Label generated");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Label generation failed");
        } finally {
            setBusyId(null);
        }
    };

    const onNotify = async (id) => {
        setBusyId(id);
        try {
            const { data: res } = await api.post(
                `/admin/fulfillment/${id}/notify`,
            );
            if (res.sent) {
                toast.success("Vessel in Transit email dispatched");
            } else if (res.mocked) {
                toast.message("Notify mocked", {
                    description: `Reason: ${res.reason}`,
                });
            } else {
                toast.error(`Notify failed: ${res.reason || "unknown"}`);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Notify failed");
        } finally {
            setBusyId(null);
        }
    };

    const c = data.counts || {};

    return (
        <div className="bg-cream min-h-screen" data-testid="privateer-page">
            <section className="max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-20">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="divider-rule text-brown">
                            <Anchor size={12} /> Privateer · Fulfillment Dashboard
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl text-forest mt-3 leading-[1.05]">
                            The Wharf.
                        </h1>
                        <p className="text-brown mt-3 max-w-xl text-sm">
                            Every paid lifestyle order arrives here for label
                            generation (Shippo) and customer notification
                            (Resend). Shipping cost is isolated from the 70/20
                            ledger split — it is a passthrough to the carrier.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="btn-pill btn-ghost text-xs"
                        data-testid="privateer-refresh-btn"
                    >
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                {/* Integration status pills */}
                <div className="mt-6 flex flex-wrap gap-2 text-[0.6rem] tracking-[0.3em] uppercase">
                    <span
                        className={`px-3 py-1.5 rounded-full border ${
                            data.shippo_configured
                                ? "bg-olive/20 text-forest border-olive/40"
                                : "bg-gold/20 text-brown border-gold/60"
                        }`}
                        data-testid="status-shippo"
                    >
                        Shippo · {data.shippo_configured ? "Live" : "Mocked"}
                    </span>
                    <span
                        className={`px-3 py-1.5 rounded-full border ${
                            data.resend_configured
                                ? "bg-olive/20 text-forest border-olive/40"
                                : "bg-gold/20 text-brown border-gold/60"
                        }`}
                        data-testid="status-resend"
                    >
                        Resend · {data.resend_configured ? "Live" : "Mocked"}
                    </span>
                    <span
                        className={`px-3 py-1.5 rounded-full border ${
                            data.printify_configured
                                ? "bg-olive/20 text-forest border-olive/40"
                                : "bg-gold/20 text-brown border-gold/60"
                        }`}
                        data-testid="status-printify"
                    >
                        Printify · {data.printify_configured ? "Live" : "Mocked"}
                    </span>
                </div>

                {/* Stats */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard
                        label="Total Queue"
                        value={c.total ?? 0}
                        testid="stat-total"
                    />
                    <StatCard
                        label="Awaiting Label"
                        value={c.pending ?? 0}
                        testid="stat-pending"
                    />
                    <StatCard
                        label="Labeled"
                        value={c.labeled ?? 0}
                        testid="stat-labeled"
                    />
                    <StatCard
                        label="Notified"
                        value={c.notified ?? 0}
                        testid="stat-notified"
                    />
                    <StatCard
                        label="Submitted to Printify"
                        value={c.submitted ?? 0}
                        accent
                        testid="stat-submitted"
                    />
                </div>

                {/* Queue */}
                <div className="mt-10">
                    <h2 className="font-serif text-2xl text-forest mb-4">
                        Manifest Queue
                    </h2>
                    {loading ? (
                        <p className="text-brown/70 text-sm italic">Loading…</p>
                    ) : data.items.length === 0 ? (
                        <div className="p-8 bg-cream-warm rounded-sm border border-dashed border-forest/20 text-center">
                            <Package
                                size={28}
                                className="mx-auto text-brown/50"
                                aria-hidden
                            />
                            <p className="mt-3 text-sm text-brown/70 italic">
                                No vessels in the queue. The wharf activates the
                                moment a Modern Director Bundle (or any
                                lifestyle SKU) is paid.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.items.map((row) => (
                                <FulfillmentRow
                                    key={row.id}
                                    row={row}
                                    onLabel={onLabel}
                                    onNotify={onNotify}
                                    busyId={busyId}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
